import { useState } from "react";
import { BrandSelect } from "../generate/BrandSelect.js";
import { PRODUCT_CATEGORIES } from "../templates/catalogOptions.js";
import { AddOptionRow } from "../ui/AddOptionRow.js";
import { CatalogSelect, type CatalogOption } from "../ui/CatalogSelect.js";
import { ErrorNotice, RequiredFieldError } from "../ui/ErrorNotice.js";
import { InlineRetry } from "../ui/InlineRetry.js";

const ADD_MODEL = "__add_model__";

/** Product type, Brand and Model — one set of pickers over the phone catalog, for the Generate
 *  page, the case step and a customer's Details. Rendered inside the caller's details grid.
 *  `onAddModel` (admins) saves a new model under the chosen brand; it resolves true on success.
 *  A brand typed in "+ Add new brand…" opens the add-model row with it. */
export function ProductPickers({
  idPrefix, categoryTestId, modelTestId, retryTestId,
  categories = PRODUCT_CATEGORIES, category, onCategory, brand, onBrand, brandOptions, allowAddBrand,
  modelId, onModel, models, modelsError, onRetryModels, onAddModel, validate = false,
}: {
  validate?: boolean;
  idPrefix: string;
  categoryTestId: string;
  modelTestId: string;
  retryTestId: string;
  categories?: readonly CatalogOption[];
  category: string;
  onCategory: (value: string) => void;
  brand: string;
  onBrand: (brand: string) => void;
  brandOptions: string[];
  allowAddBrand: boolean;
  modelId: string;
  onModel: (id: string) => void;
  models: CatalogOption[];
  modelsError: boolean;
  onRetryModels: () => void;
  onAddModel?: (model: string) => Promise<boolean>;
}) {
  const [addingModel, setAddingModel] = useState(false);
  const canAddModel = !!onAddModel && !!brand.trim();
  const pickModel = (id: string) => {
    setAddingModel(id === ADD_MODEL);
    if (id !== ADD_MODEL) onModel(id);
  };
  const addModel = async (name: string) => {
    if (await onAddModel!(name)) setAddingModel(false);
  };
  // A brand is in the catalog once a model is saved under it, so a new brand asks for one at once.
  const chooseBrand = (next: string) => {
    onBrand(next);
    setAddingModel(!!onAddModel && !brandOptions.some((b) => b.toLowerCase() === next.toLowerCase()) && !!next.trim());
  };
  return (
    <>
      <div className="field">
        <label className="label" htmlFor={`${idPrefix}-type`}>Product type</label>
        <select id={`${idPrefix}-type`} className="input" value={category}
          onChange={(e) => onCategory(e.target.value)} data-testid={categoryTestId}
          aria-invalid={validate && !category || undefined}
          aria-describedby={validate && !category ? `${idPrefix}-type-error` : undefined}>
          {!category && <option value="">— Select —</option>}
          {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        {validate && !category && <RequiredFieldError id={`${idPrefix}-type-error`} field="Product type" />}
      </div>
      {/* A div, not a label: BrandSelect renders its own select plus an inline "add" row. */}
      <div className="field">
        <span className="label">Brand<span aria-hidden="true">*</span></span>
        <BrandSelect value={brand} onChange={chooseBrand} options={brandOptions}
          allowAdd={allowAddBrand} required invalid={validate && !brand.trim()}
          describedBy={validate && !brand.trim() ? `${idPrefix}-brand-error` : undefined} />
        {validate && !brand.trim() && <RequiredFieldError id={`${idPrefix}-brand-error`} field="Brand" />}
      </div>
      <div className="field">
        <label className="label" htmlFor={`${idPrefix}-model`}>
          Model<span aria-hidden="true">*</span>
          {modelsError && <InlineRetry onRetry={onRetryModels} testId={retryTestId} />}
        </label>
        <CatalogSelect id={`${idPrefix}-model`} value={addingModel ? ADD_MODEL : modelId} onChange={pickModel}
          testId={modelTestId} error={modelsError} required invalid={validate && !modelId}
          describedBy={modelsError || validate && !modelId ? `${idPrefix}-model-error` : undefined}
          options={canAddModel ? [...models, { value: ADD_MODEL, label: "+ Add new model…" }] : models} />
        {modelsError ? <span id={`${idPrefix}-model-error`} className="field-error" role="alert"><ErrorNotice detail="network: model options could not load" /></span>
          : validate && !modelId && <RequiredFieldError id={`${idPrefix}-model-error`} field="Model" />}
        {addingModel && <AddOptionRow placeholder="New model name" testId="product-model" onAdd={(n) => void addModel(n)} />}
      </div>
    </>
  );
}
